import { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import BottomDrawer from '../../components/common/BottomDrawer';
import { InputBackOffice } from '../../components/common/InputBackoffice';
import Spinner from '../../components/common/Spinner';
import { ErrorToast } from '../../components/common/Toasts/Toasts';
import check from '../../assets/icons/check.svg';
import search from '../../assets/icons/search.svg';
import { P2, P3 } from '../../styles/FontStyles';
import {
  ArrowPolygon,
  AuthLayoutTitle,
  Select
} from '../../styles/GlobalComponents';
import {
  FlexCenter,
  Label,
  SInput,
  SInputContainer
} from '../../styles/stylesStyled';
import { RouteComponentProps } from 'react-router';
import { useAppDispatch, useAppSelector } from '../../app/hooks';
import { Button } from '../../components';
import countries from '../../lib/countries';
import { colors } from '../../styles/GlobalStyles';
import { updateProfileDetailsWithWallet } from './ProfileDetails.actions';
import moment from 'moment';
import { useAccount } from 'wagmi';
import { LOGIN } from '../../routes/routes';

const API_URL = process.env.REACT_APP_API_URL;

const ProfileDetails = ({ history }: RouteComponentProps) => {
  const { t } = useTranslation('onboarding');
  const dispatch = useAppDispatch();

  const { loading, register, userInfo } = useAppSelector((state) => state.auth);
  const { referrerId, approvalDate } = register;

  const [email, setEmail] = useState('');
  const [username, setUsername] = useState('');
  const [captchaLoading] = useState(false);

  const [searchTerm, setSearchTerm] = useState('');
  const [country, setCountry] = useState('');
  const [userCountry] = useState('');
  const [countryCode, setCountryCode] = useState('');
  const [walletAddress, setWalletAddress] = useState('');

  const [countryList, setCountryList] = useState(countries);
  const [countryImage, setCountryImage] = useState('');

  const [sessionId, setSessionId] = useState<string | null>(null);
  const [waitingApproval, setWaitingApproval] = useState(false);

  const [isVisible, setIsVisible] = useState(false);

  const eventSourceRef = useRef<EventSource | null>(null);

  const { address } = useAccount();

  const onClose = useCallback(() => {
    setIsVisible(false);
  }, []);

  // 🔥 CONFIRM (signup)
  const confirm = async () => {
    const res: any = await dispatch(
      updateProfileDetailsWithWallet({
        referrerId: referrerId ? referrerId : userInfo?.user?.id,
        username,
        approvalDate: approvalDate
          ? approvalDate
          : moment().utc().format('YYYY-MM-DD[T]HH:mm:ss.SSS[Z]'),
        email,
        country,
        countryCode,
        walletAddress: walletAddress || address
      })
    );

    if (res.meta.requestStatus === 'fulfilled') {
      setSessionId(res.payload);
      setWaitingApproval(true);
    }
  };

  // 🔥 SSE LISTENER
  useEffect(() => {
    if (!sessionId) return;

    // // cleanup ako postoji stara konekcija
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
    }

    const es = new EventSource(
      `${API_URL}/flipme-auth/signup/status?sessionId=${sessionId}`
    );

    const approvalTimeout = setTimeout(() => {
      console.warn('SSE timeout - user did not approve in time');
      es.close();
      setWaitingApproval(false);
      ErrorToast('Approval timeout. Please try again.');
    }, 60000);

    eventSourceRef.current = es;

    es.addEventListener('approved', (event: any) => {
      try {
        const parsed = JSON.parse(event.data);

        if (parsed.status === 'approved') {
          clearTimeout(approvalTimeout);
          es.close();
          setWaitingApproval(false);
          history.push(LOGIN);
        }
      } catch (err) {
        console.error('approved parse error', err);
      }
    });

    es.onmessage = (event) => {
      try {
        const parsed = JSON.parse(event.data);

        if (parsed.status === 'approved') {
          clearTimeout(approvalTimeout);
          es.close();
          setWaitingApproval(false);
          history.push(LOGIN);
        }
      } catch (err) {
        console.error('message parse error', err);
      }
    };

    es.onerror = (err: any) => {
      console.error('SSE error:', err);
      es.close();
      setWaitingApproval(false);
      clearTimeout(approvalTimeout);

      if (err?.data) {
        try {
          const parsed = JSON.parse(err?.data);

          ErrorToast(parsed?.message);
        } catch (e) {
          ErrorToast('Connection lost. Please try again.');
        }
      } else {
        ErrorToast('Connection lost. Please try again.');
      }
    };

    return () => {
      clearTimeout(approvalTimeout);
      es.close();
    };
  }, [sessionId, history]);

  const checkCountry = (country: any) => {
    setCountryList(
      countryList.map((item) =>
        item.name === country.name
          ? { ...item, checked: true }
          : { ...item, checked: false }
      )
    );
  };

  const Required = () => <span style={{ color: colors.error500 }}>*</span>;

  return (
    <>
      {(loading || captchaLoading) && <Spinner fullScreen={true} transparent />}

      <AuthLayoutTitle>Welcome to Mentus!</AuthLayoutTitle>

      <P3 center mb='40'>
        BASIC INFORMATIONS
      </P3>

      <Label>
        VeilOn wallet address <Required />
      </Label>

      <InputBackOffice
        id='walletAddress'
        type='text'
        placeholder='Enter your VeilOn wallet address'
        name='walletAddress'
        value={walletAddress.replace(/\s+/g, '')}
        onChange={(e: string) => setWalletAddress(e.replace(/\s+/g, ''))}
      />

      <Label>
        {t('profile_details.usernameLabel')}
        <Required />
      </Label>

      <InputBackOffice
        id='username'
        type='text'
        placeholder={t('profile_details.usernamePlaceholder')}
        name='username'
        value={username.replace(/[^A-Z0-9]/gi, '') || ''}
        onChange={(e: string) =>
          setUsername(e.replace(/[^A-Z0-9]/gi, '').toLowerCase())
        }
      />

      <Label>
        Email <Required />
      </Label>

      <InputBackOffice
        id='email'
        type='text'
        placeholder={t('email_address.enter_your_email_placeholder')}
        name='email'
        value={email.replace(/\s+/g, '')}
        onChange={(e: string) => setEmail(e.replace(/\s+/g, ''))}
      />

      <div>
        <Select
          onClick={() => setIsVisible(true)}
          style={{ cursor: 'pointer' }}>
          <FlexCenter>
            {countryImage && (
              <img
                src={countryImage}
                alt='flag'
                style={{ marginRight: 20, width: 28, height: 20 }}
              />
            )}
            <div>{country || t('profile_details.countryLabel')}</div>
          </FlexCenter>
          <ArrowPolygon direction={''} />
        </Select>
      </div>

      <Button
        m='30px 0 10px 0'
        disabled={
          username === '' ||
          email === '' ||
          countryCode === '' ||
          waitingApproval
        }
        onClick={confirm}>
        {waitingApproval
          ? 'Waiting for approval...'
          : t('profile_details.continue')}
      </Button>

      <BottomDrawer isVisible={isVisible} onClose={onClose} height='90vh'>
        <SInputContainer>
          <img src={search} alt='search.svg' />
          <SInput
            placeholder={t('profile_details.countryLabelPlaceholder')}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </SInputContainer>

        {countryList &&
          countryList
            ?.sort((a) => (a.name === userCountry ? -1 : 1))
            .filter((value) =>
              searchTerm === ''
                ? true
                : value.name.toLowerCase().includes(searchTerm.toLowerCase())
            )
            .map((country, i) => {
              if (country.hidden) return null;

              return (
                <div
                  key={i}
                  onClick={() => {
                    setCountry(country.name);
                    setCountryCode(country.code);
                    setCountryImage(country.flag);
                    setIsVisible(false);
                    checkCountry(country);
                  }}
                  style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer'
                  }}>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      marginBottom: 20
                    }}>
                    <img
                      src={country.flag}
                      alt='flag'
                      style={{ marginRight: 20, width: 28, height: 20 }}
                    />
                    <P2>{country.name}</P2>
                  </div>

                  {country.checked && (
                    <img src={check} alt='check' style={{ marginBottom: 30 }} />
                  )}
                </div>
              );
            })}
      </BottomDrawer>
    </>
  );
};

export default ProfileDetails;
